import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { selectors as uiSelectors, actions as uiActions } from 'state/ui';
// styles
import styled, { css } from 'styled-components';
import { media, zIndexses } from 'utils/style';
// components
import LogoButton from 'refactored/ui/nav/logoButton';
import AppBarSeparator from './appBarSeparator';
import SearchBar from './searchBar';
import UserInfo from './userInfo';
import AppBarNewsButton from './appBarNewsButton';
import SecondaryMenuButton from './secondaryMenuButton';
import SecondaryMenuContainer from './secondaryMenuContainer';
import SecondaryMenuEntries from './secondaryMenuEntries';
import ModalFaqs from 'components/ModalFaqs';
import { SERVICES } from 'tenant/utils';

const AppBar = ({ className, handleBack, isLoading = false }) => {
  const dispatch = useDispatch();

  const isFaqsModalOpened = useSelector(uiSelectors.isFaqsModalOpened);
  const isSecondaryMenuOpened = useSelector(uiSelectors.isSecondaryMenuOpened);

  const toggleMenu = useCallback(() => {
    dispatch(uiActions.toggleSecondaryMenu());
  }, [dispatch]);

  const closeMenu = useCallback(() => {
    if (isSecondaryMenuOpened) {
      dispatch(uiActions.toggleSecondaryMenu());
    }
  }, [dispatch, isSecondaryMenuOpened]);

  return (
    <nav className={className} role="navigation" aria-label="AppBar">
      <LogoButton handleBack={handleBack} isLoading={isLoading} />
      {!isLoading && (
        <>
          <AppBarSeparator />
          <SearchBar />
          <UserInfo />
          {/* this button is to open beamer, only visible on mobile */}
          {SERVICES.BEAMER && <AppBarNewsButton />}
          <SecondaryMenuButton
            onClick={toggleMenu}
            isOpened={isSecondaryMenuOpened}
          />
          <SecondaryMenuContainer isVisible={isSecondaryMenuOpened}>
            <SecondaryMenuEntries
              onEntryClick={toggleMenu}
              onClickOutside={closeMenu}
            />
          </SecondaryMenuContainer>
          {isFaqsModalOpened && <ModalFaqs />}
        </>
      )}
    </nav>
  );
};

AppBar.propTypes = {
  className: PropTypes.string,
  handleBack: PropTypes.func,
  isLoading: PropTypes.bool,
};

const regularCSS = css`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  background-color: var(--white);
  border-bottom: 1px solid var(--grey-mouse);
  position: relative;
  z-index: ${zIndexses.appBar};
  @media print {
    display: none;
  }
`;

const mobileCSS = css`
  background-color: var(--primary500);
  border-color: transparent;
`;

const StyledAppBar = styled(AppBar)`
  ${regularCSS}
  ${media.lessThan('small')`${mobileCSS}`}
`;

export default StyledAppBar;
